import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import {
  Outlet,
  useBeforeUnload,
  useBlocker,
} from 'react-router-dom';
import type { BlockerFunction } from 'react-router-dom';
import { useArchiveAccess } from '../hooks/useArchiveAccess';
import { useCheckBeforeMove } from '../hooks/useCheckBeforeMove';
import { usePageOwnerIdentityStore } from '../store/pageOwnerIdentityStore';
import { useSettingStore } from '../store/settingStore';
import { useEditModeStore } from '../store/authStore';
import { useStandardAlertStore } from '../store/portal/alertStore';
import { scrollToChangedElement } from '../hooks/useUnsavedChanges';
import { useArchiveInteractionProtection } from '../hooks/useArchiveInteractionProtection';
import { isExposureLevelSearchable } from '../util/exposureLevel';
import { useArchiveViewTracking } from '../../analytics/hook/useArchiveViewTracking';
import { shouldTrackArchiveView } from '../../analytics/application/archiveViewTrackingPolicy';

const ArchiveRouteGuard: React.FC = () => {
  const { allowAccess, isOwner } = useArchiveAccess();
  useArchiveInteractionProtection(allowAccess && !isOwner);
  const pageOwnerAui = usePageOwnerIdentityStore(
    (state) => state.pageOwner?.aui,
  );
  const setting = useSettingStore((state) => state.setting);
  const analyticsEnabled = shouldTrackArchiveView({
    allowAccess,
    isOwner,
    archiveAui: pageOwnerAui,
    settingAui: setting?.aui,
    searchable:
      setting !== null &&
      setting !== undefined &&
      isExposureLevelSearchable(setting.exposureLevel),
  });
  useArchiveViewTracking({
    archiveAui: pageOwnerAui,
    enabled: analyticsEnabled,
  });
  const { checkPageHasChanges } = useCheckBeforeMove();
  const setUnchangedAlert = useStandardAlertStore(
    (state) => state.setUnchangedAlert,
  );
  const allowAccessRef = useRef(allowAccess);
  const checkPageHasChangesRef = useRef(checkPageHasChanges);
  allowAccessRef.current = allowAccess;
  checkPageHasChangesRef.current = checkPageHasChanges;

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      return (
        allowAccessRef.current === true &&
        currentLocation.pathname !== nextLocation.pathname &&
        checkPageHasChangesRef.current()
      );
    },
    [],
  );
  const blocker = useBlocker(shouldBlock);
  const blockerRef = useRef(blocker);
  const isBlockAlertOpenRef = useRef(false);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      isBlockAlertOpenRef.current = false;
      return;
    }
    if (isBlockAlertOpenRef.current) return;

    isBlockAlertOpenRef.current = true;
    setTimeout(scrollToChangedElement, 100);
    setUnchangedAlert(
      () => {
        const currentBlocker = blockerRef.current;
        if (currentBlocker.state === 'blocked') {
          currentBlocker.proceed();
        }
      },
      () => {
        const currentBlocker = blockerRef.current;
        if (currentBlocker.state === 'blocked') {
          currentBlocker.reset();
        }
      },
    );
  }, [blocker.state, setUnchangedAlert]);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (
          allowAccessRef.current !== true ||
          !checkPageHasChangesRef.current()
        ) {
          return;
        }
        event.preventDefault();
        event.returnValue = '';
      },
      [],
    ),
    { capture: true },
  );

  useLayoutEffect(() => {
    return () => {
      usePageOwnerIdentityStore.getState().clearPageOwner();
      useSettingStore.getState().clearSetting();
      useEditModeStore.getState().setIsEditMode(false);
    };
  }, []);

  return allowAccess ? <Outlet /> : null;
};

export default ArchiveRouteGuard;
